import React from 'react';
import {createPortal} from 'react-dom';

import {dialogCloseStyle, dialogHeaderStyle, dialogTitleStyle, drawerPanelStyle} from '../lib/dialogStyles.js';
import {
  DialogContext,
  DiscardPrompt,
  MODAL_INITIAL_FOCUS,
  closeDecision,
  useDialogPortal,
  type CloseSource,
} from './Modal.js';
import {useModalFocusTrap} from './useModalFocusTrap.js';

// ============================================================================
// Drawer — view or edit a record without leaving a list (Design Constitution
// O04, O09, O05, O06, Q38)
// ----------------------------------------------------------------------------
// From the right, ~480px; a bottom sheet on phones (index.html). Same rules as
// the modal shell: title + ×, Esc closes only this top-most layer, the
// backdrop closes only if nothing was typed, typed data asks 'Discard
// Changes?', focus starts on the first field and returns to the opener. A
// drawer holding real work has a URL (N14): the caller opens it from a route
// or query value. 'How To Use' help opens in a drawer (O09).
// Slides 24px over 180ms (reduced motion: fade only), from index.html.
// ============================================================================

type DrawerProps = {
  title: React.ReactNode;
  onClose: () => void;
  dirty?: boolean;
  autosave?: boolean;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  drawerAttrs?: Record<string, string>;
  requestDiscardRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
};

export default function Drawer({
  title,
  onClose,
  dirty = false,
  autosave = false,
  footer,
  children,
  drawerAttrs,
  requestDiscardRef,
}: DrawerProps) {
  const [typed, setTyped] = React.useState(false);
  const [asking, setAsking] = React.useState(false);
  const hasTyped = dirty || typed;
  const titleId = React.useId();
  const discardResolverRef = React.useRef<((discard: boolean) => void) | null>(null);
  const resolvePendingDiscard = (discard: boolean) => {
    const resolve = discardResolverRef.current;
    discardResolverRef.current = null;
    resolve?.(discard);
  };
  if (requestDiscardRef) {
    requestDiscardRef.current = () => {
      if (!hasTyped || autosave) {
        onClose();
        return Promise.resolve(true);
      }
      return new Promise<boolean>((resolve) => {
        discardResolverRef.current = resolve;
        setAsking(true);
      });
    };
  }
  React.useEffect(() => {
    return () => {
      if (requestDiscardRef) requestDiscardRef.current = null;
      const resolve = discardResolverRef.current;
      discardResolverRef.current = null;
      resolve?.(false);
    };
  }, [requestDiscardRef]);
  const requestClose = (source: CloseSource) => {
    const decision = closeDecision(source, hasTyped, autosave);
    if (decision === 'close') onClose();
    else if (decision === 'ask') setAsking(true);
  };
  const {dialogRef, handleDialogKeyDown} = useModalFocusTrap<HTMLDivElement>({
    onCancel: () => requestClose('esc'),
    initialFocusSelector: MODAL_INITIAL_FOCUS,
  });

  const layer = (
    <div
      data-dialog-scrim="drawer"
      onMouseDown={(e) => {
        // A click on the scrim must not move focus out of the dialog, or Esc
        // (handled on the dialog) would stop working (O06).
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        requestClose('backdrop');
      }}
      style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'var(--scrim)'}}
    >
      <div
        {...drawerAttrs}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-drawer-panel="1"
        onInput={() => {
          if (!typed) setTyped(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') e.stopPropagation();
          handleDialogKeyDown(e);
        }}
        style={drawerPanelStyle}
      >
        <div style={{...dialogHeaderStyle, borderBottom: '1px solid var(--border)', paddingBottom: 14}}>
          <h2 id={titleId} style={dialogTitleStyle}>
            {title}
          </h2>
          <button
            type="button"
            className="wcf-clickable"
            aria-label="Close"
            data-dialog-close="1"
            onClick={() => requestClose('close')}
            style={dialogCloseStyle}
          >
            ×
          </button>
        </div>
        <div data-dialog-body="1" style={{flex: 1, overflowY: 'auto', padding: '16px 24px'}}>
          {children}
        </div>
        {footer && (
          <div style={{borderTop: '1px solid var(--border)'}}>
            <DialogContext.Provider value={{requestClose, autosave}}>{footer}</DialogContext.Provider>
          </div>
        )}
      </div>
      {asking && (
        <DiscardPrompt
          onKeep={() => {
            setAsking(false);
            resolvePendingDiscard(false);
          }}
          onDiscard={() => {
            setAsking(false);
            resolvePendingDiscard(true);
            onClose();
          }}
        />
      )}
    </div>
  );
  const target = useDialogPortal();
  return target ? createPortal(layer, target) : layer;
}
